import mqtt from 'mqtt';
import { effectScope, type MaybeRefOrGetter, toValue } from 'vue';
import { tryOnScopeDispose, watchImmediate } from '@vueuse/core';
import { Log } from '@devanjs/log';
import { captureException } from '@sentry/vue';
import { useAuthToken } from '@/services/auth';

type SubscriptionHandler = (payload: Uint8Array, topic: string) => void;
type ClientHandlers = Map<
  string,
  {
    listener: mqtt.OnMessageCallback;
    handlers: Set<SubscriptionHandler>;
  }
>;

const topicMatch = (filter: string, topic: string, handleSharedSubscription = false): boolean => {
  const filterArray = filter.split('/');

  if (handleSharedSubscription && filterArray.length > 2 && filter.startsWith('$share/')) {
    filterArray.splice(0, 2);
  }

  const length = filterArray.length;
  const topicArray = topic.split('/');

  for (let i = 0; i < length; ++i) {
    const left = filterArray[i];
    const right = topicArray[i];
    if (left === '#') return topicArray.length >= length - 1;
    if (left !== '+' && left !== right) return false;
  }

  return length === topicArray.length;
};

const clientId = (Math.random() + 1).toString(36).substring(7);

const clients: Record<
  string,
  {
    client: mqtt.MqttClient;
    handlers: ClientHandlers;
  }
> = {};

const createClient = (
  url: string,
  username: MaybeRefOrGetter<string | undefined>,
): {
  client: mqtt.MqttClient;
  handlers: ClientHandlers;
} => {
  const scope = effectScope(true);

  const client = scope.run(() => {
    Log.tag('mqtt').green('Initialise').info(url, clientId);

    const token = useAuthToken({ autoRefresh: true });

    const client = mqtt.connect(url, {
      protocolVersion: 5,
      transformWsUrl: (url, _options, client) => {
        client.options.username = toValue(username) || 'anonymous';
        client.options.password = toValue(token) || '';
        client.options.clientId = `[${client.options.username}@${clientId}]-`;

        return url;
      },
      keepalive: 30,
      reconnectPeriod: 10 * 1000,
      connectTimeout: 10 * 1000,
      manualConnect: true,
    });

    watchImmediate(
      () => ({
        username: toValue(username),
        password: toValue(token),
      }),
      ({ username, password }) => {
        if (username && password && !client.connected) {
          Log.tag('mqtt').green('Connecting').info('as', username);

          client.connect();
        }
      },
    );

    client.on('connect', () => {
      Log.tag('mqtt').green('Connected').info();
    });

    client.on('reconnect', () => {
      Log.tag('mqtt').green('Reconnecting').info();
    });

    client.on('disconnect', () => {
      Log.tag('mqtt').green('Disconnected').info();
    });

    client.on('close', () => {
      Log.tag('mqtt').green('Closed').info();
    });

    client.on('error', (error) => {
      Log.tag('mqtt').red('Error').error(error);

      const message = error?.message ?? '';

      if (message.includes('Connection refused')) {
        client.end(true);
        return;
      }

      if (message.includes('connack timeout')) {
        return;
      }
      captureException(error);
    });

    client.on('message', (topic) => {
      Log.tag('mqtt').green('Message').info(topic);
    });

    return client;
  });

  if (client === undefined) {
    throw new Error('Failed to create MQTT client');
  }

  tryOnScopeDispose(() => {
    if (clients[url].handlers.size > 0) {
      return;
    }

    delete clients[url];
    scope.stop();

    if (client.connected) {
      client.end();
    }
  });

  return {
    client,
    handlers: new Map(),
  };
};

export const useMqtt = (url: string, username: MaybeRefOrGetter<string | undefined>) => {
  const { client, handlers } = clients[url]
    ? clients[url]
    : (clients[url] = createClient(url, username));

  const subscribe = (topic: string, handler: SubscriptionHandler) => {
    if (handlers.get(topic) === undefined) {
      const listener = (messageTopic: string, message: Uint8Array) => {
        if (topicMatch(topic, messageTopic)) {
          handlers.get(topic)?.handlers.forEach((handler) => handler(message, messageTopic));
        }
      };

      client.on('message', listener);
      client.subscribe(topic, { qos: 1 });

      handlers.set(topic, {
        listener,
        handlers: new Set(),
      });

      Log.tag('mqtt').green('Subscribe').info('to', topic);
    }

    handlers.get(topic)?.handlers.add(handler);
    Log.tag('mqtt').green('Adding handler').info('to', topic);

    tryOnScopeDispose(() => unsubscribe(topic, handler));
  };

  const unsubscribe = (topic: string, handler?: SubscriptionHandler) => {
    const topicHandlers = handlers.get(topic);

    if (topicHandlers === undefined) {
      return;
    }

    if (handler !== undefined) {
      topicHandlers.handlers.delete(handler);
      Log.tag('mqtt').green('Removing handler').info('from', topic);
    }

    if ((handler === undefined || topicHandlers.handlers.size) === 0) {
      handlers.delete(topic);
      client.unsubscribe(topic);
      client.off('message', topicHandlers.listener);

      Log.tag('mqtt').green('Unsubscribe').info('from', topic);
    }
  };

  const publish = (topic: string, payload: string) => {
    client.publish(topic, payload, { qos: 1 });
    Log.tag('mqtt').green('Publish').info('to', topic);
  };

  return {
    subscribe,
    unsubscribe,
    publish,
  };
};
